<template>
  <!--
    ProjectCard — renders one normalized `Project` view model produced by
    client/src/services/projectNormalizer.js (title, image with alt text, and
    Service_Category). Requirements:
      - 6.2: display title, image, and Service_Category.
      - 9.3: provide a text alternative for the (informative) project image,
        including the branded placeholder shown when no image is available.
      - 11.4: tolerate missing/optional fields and never fail to render — a
        branded placeholder is shown when `imageUrl` is null.
  -->
  <v-card class="project-card" rounded="lg" elevation="2">
    <!-- Image when present; otherwise a branded placeholder. -->
    <v-img
      v-if="imageUrl"
      :src="imageUrl"
      :alt="imageAlt"
      :aspect-ratio="16 / 9"
      cover
      loading="lazy"
      class="project-card__image"
    />
    <div
      v-else
      class="project-card__placeholder"
      role="img"
      :aria-label="imageAlt"
    >
      <!-- Inline, self-contained branded glyph so the placeholder renders
           regardless of icon-font availability. -->
      <svg
        class="project-card__placeholder-glyph"
        viewBox="0 0 64 64"
        aria-hidden="true"
        focusable="false"
      >
        <path
          fill="currentColor"
          d="M8 12h48a2 2 0 0 1 2 2v36a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V14a2 2 0 0 1 2-2Zm2 4v25.2l11.5-11.5a2 2 0 0 1 2.8 0L33 38.4l9.7-9.7a2 2 0 0 1 2.8 0L54 37.2V16H10Zm34-2.5a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9Z"
        />
      </svg>
    </div>

    <v-card-item>
      <!-- Service_Category shown as an accent overline above the title. -->
      <div class="project-card__category text-uppercase">
        {{ serviceCategory }}
      </div>
      <v-card-title class="project-card__title">
        {{ title }}
      </v-card-title>
      <v-card-text v-if="summary" class="project-card__summary px-0 pt-1">
        {{ summary }}
      </v-card-text>
    </v-card-item>
  </v-card>
</template>

<script>
// ProjectCard — presents a single normalized project. It reads the view-model
// fields defensively (with the same safe defaults as projectNormalizer) so it
// renders gracefully even when handed a partially-populated object directly
// (Requirement 11.4).
const DEFAULT_TITLE = 'Untitled project'
const DEFAULT_CATEGORY = 'Other'

export default {
  name: 'ProjectCard',
  props: {
    // A normalized `Project` view model (see projectNormalizer.js).
    project: {
      type: Object,
      required: true,
    },
  },
  computed: {
    // Trimmed title with a safe fallback (Requirement 11.4).
    title () {
      const value = this.project && this.project.title
      return typeof value === 'string' && value.trim() !== ''
        ? value.trim()
        : DEFAULT_TITLE
    },
    // Service_Category with a safe fallback (Requirements 6.2, 11.4).
    serviceCategory () {
      const value = this.project && this.project.serviceCategory
      return typeof value === 'string' && value.trim() !== ''
        ? value.trim()
        : DEFAULT_CATEGORY
    },
    // Image URL, or null to trigger the branded placeholder (Requirement 11.4).
    imageUrl () {
      const value = this.project && this.project.imageUrl
      return typeof value === 'string' && value.trim() !== ''
        ? value.trim()
        : null
    },
    // Text alternative for the image / placeholder (Requirement 9.3).
    imageAlt () {
      const value = this.project && this.project.imageAlt
      if (typeof value === 'string' && value.trim() !== '') {
        return value.trim()
      }
      return `${this.title} project image`
    },
    // Optional short summary; rendered only when present.
    summary () {
      const value = this.project && this.project.summary
      return typeof value === 'string' ? value.trim() : ''
    },
  },
}
</script>

<style scoped>
.project-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.project-card__image {
  width: 100%;
}

/* Branded placeholder uses the brand brown surface with a centred glyph. */
.project-card__placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: linear-gradient(135deg, #41281b 0%, #5a3826 100%);
  color: rgba(242, 148, 0, 0.85); /* brand burnt orange */
}

.project-card__placeholder-glyph {
  width: 28%;
  max-width: 96px;
  height: auto;
}

.project-card__category {
  color: #e7511e; /* brand red-orange accent */
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.project-card__title {
  padding-left: 0;
  padding-right: 0;
  font-weight: 700;
  line-height: 1.25;
  white-space: normal;
  word-break: break-word;
}

.project-card__summary {
  color: #4a4a4a; /* brand neutral text */
}
</style>
